import { useQuery, UseQueryOptions } from 'react-query';
import { usePublicApiClient } from '../usePublicApiClient';
import { FulfilmentClient } from '@jbhi-fi/apis/src/fulfilment';
import { FulfilmentOptionsLineItem, FulfilmentOptionsResponse } from '@jbhi-fi/apis/src/fulfilment/types';
import { useRecaptchaContext } from '../../context/RecaptchaProvider';
import { FULFILMENT_API_RECAPTCHA_ACTION } from '@jbhi-fi/apis/src/fulfilment/FulfilmentClient';
import { CountryCode } from '@jbhi-fi/apis/src/utils/multiTenancyHeaders';

export type UseFulfilmentOptionsResult = { data?: FulfilmentOptionsResponse; isLoading: boolean; error?: unknown };

type UseFulfilmentOptionsProps = {
  fulfilmentClient?: FulfilmentClient;
  isEnabled?: boolean;
  address: { countryCode: CountryCode; postcode: string; suburb: string } | undefined;
  lineItems: Array<FulfilmentOptionsLineItem>;
  requiresScheduledOptions?: boolean;
} & Pick<UseQueryOptions, 'cacheTime' | 'staleTime'>;

export const useFulfilmentOptions = ({
  fulfilmentClient: fulfilmentClientProp,
  address,
  lineItems,
  isEnabled,
  requiresScheduledOptions = true,
  ...useQueryOptions
}: UseFulfilmentOptionsProps): UseFulfilmentOptionsResult => {
  const { fulfilmentClient: fulfilmentClientContext } = usePublicApiClient();
  const fulfilmentClient = fulfilmentClientProp || fulfilmentClientContext;
  const { generateToken, isReady: isRecaptchaReady } = useRecaptchaContext();

  if (!fulfilmentClient) {
    throw new Error('useFulfilmentOptions must be used with a contentfulThemeApiClient');
  }

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'fulfilmentOptions',
      { countryCode: address?.countryCode, postcode: address?.postcode, suburb: address?.suburb },
      lineItems.map(lineItem => ({
        sku: lineItem.SKU ?? '',
        quantity: lineItem.Quantity ?? 1,
        lineItemFreightPrice: lineItem.LineItemFreightPrice ?? 0,
        giftCardType: lineItem.GiftCardType ?? null,
        tieredIds: lineItem.TieredIds,
      })),
    ],
    queryFn: async () => {
      const recaptchaToken = await generateToken?.(FULFILMENT_API_RECAPTCHA_ACTION);

      const response = await fulfilmentClient?.getOptions(
        {
          Address: {
            CountryCode: address?.countryCode ?? 'AU',
            Postcode: address?.postcode ?? '',
            Suburb: address?.suburb ?? '',
          },
          LineItems: lineItems,
          requiresScheduledOptions,
        },
        recaptchaToken ?? ''
      );
      if (response.error) {
        throw `Failed to fetch availability: ${response?.error?.message} ${response?.statusCode}`;
      }
      return response;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    enabled: isEnabled && isRecaptchaReady && Boolean(lineItems?.length) && Boolean(address?.postcode),
    ...useQueryOptions,
  });

  return { isLoading: isLoading || !isRecaptchaReady, data: data?.data, error };
};
